import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkle, Plus, CheckCircle, CalendarBlank, ArrowRight } from "@phosphor-icons/react";

/**
 * CustomerVasAndOrdersPanel — Endkunde bucht VAS auf sein Abo + legt Sonderaufträge an.
 * Props: brandColor, subscriptions (aktive Abos), onRefresh
 */
export default function CustomerVasAndOrdersPanel({ brandColor = "#2A86A8", subscriptions = [], onRefresh }) {
  const [addons, setAddons] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [newOrder, setNewOrder] = useState({ scheduled_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), notes: "", addon_service_ids: [] });

  const load = async () => {
    try {
      const [a, o] = await Promise.all([api.get("/addon-services"), api.get("/special-orders")]);
      setAddons(a.data);
      setOrders(o.data);
    } catch (e) { console.warn("[CustomerVasAndOrdersPanel] load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const attach = async (subId, serviceId, isAttached) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/subscriptions/${subId}/addons`, { service_id: serviceId, action: isAttached ? "remove" : "add" });
      setMsg(isAttached ? "VAS entfernt" : "VAS aktiviert");
      onRefresh?.();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const createOrder = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post("/special-orders", {
        scheduled_date: newOrder.scheduled_date,
        notes: newOrder.notes,
        addon_service_ids: newOrder.addon_service_ids,
      });
      setNewOrder({ scheduled_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), notes: "", addon_service_ids: [] });
      setMsg("Sonderauftrag angelegt — wir bestätigen den Termin per E-Mail");
      load();
      onRefresh?.();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const toggleAddonInOrder = (sid) => {
    const has = newOrder.addon_service_ids.includes(sid);
    setNewOrder({ ...newOrder, addon_service_ids: has ? newOrder.addon_service_ids.filter((x) => x !== sid) : [...newOrder.addon_service_ids, sid] });
  };

  const eur = (c) => `€${(c / 100).toFixed(2)}`;
  const activeSubs = subscriptions.filter((s) => s.status !== "canceled");

  return (
    <div className="space-y-6" data-testid="customer-vas-panel">
      {/* VAS auf Abo */}
      <div className="bg-white rounded-2xl border border-black/5 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={20} weight="bold" style={{ color: brandColor }} />
          <h3 className="font-bold text-lg">Zusatzdienste</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Erweitern Sie Ihr Abo z. B. um Filterwechsel oder Innendesinfektion.</p>
        {addons.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3">Aktuell keine Zusatzdienste angeboten.</p>
        ) : activeSubs.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3">Sie haben noch kein aktives Abo — buchen Sie zuerst eine Reinigung.</p>
        ) : (
          <div className="space-y-3">
            {activeSubs.map((sub) => (
              <div key={sub.subscription_id} className="border border-black/5 rounded-lg p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Abo — {sub.frequency} · {eur(sub.price_per_service_cents)}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {addons.map((a) => {
                    const isAttached = (sub.addon_service_ids || []).includes(a.service_id);
                    return (
                      <label key={a.service_id} className={`flex items-start gap-2 p-3 border rounded-md cursor-pointer transition ${isAttached ? "border-emerald-300 bg-emerald-50" : "border-black/10 hover:border-black/20"}`} data-testid={`vas-attach-${sub.subscription_id}-${a.service_id}`}>
                        <input type="checkbox" checked={isAttached} onChange={() => attach(sub.subscription_id, a.service_id, isAttached)} disabled={busy} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{a.name} <span className="text-xs text-zinc-500 font-normal">· {eur(a.price_cents)}</span></div>
                          {a.description && <div className="text-xs text-zinc-500">{a.description}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sonderauftrag */}
      <div className="bg-white rounded-2xl border border-black/5 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Plus size={20} weight="bold" style={{ color: brandColor }} />
          <h3 className="font-bold text-lg">Sonderauftrag</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Einmalige Reinigung außerhalb Ihres Abo-Rhythmus, z. B. nach Umzug oder besonderem Anlass.</p>

        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-xs">Wunschtermin</Label>
            <Input type="date" className="h-10 rounded-md" value={newOrder.scheduled_date} onChange={(e) => setNewOrder({ ...newOrder, scheduled_date: e.target.value })} data-testid="order-date" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Bemerkung / Besonderheiten</Label>
            <Input className="h-10 rounded-md" placeholder="z. B. Bio-Tonne stark verunreinigt" value={newOrder.notes} onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })} data-testid="order-notes" />
          </div>
        </div>

        {addons.length > 0 && (
          <div className="mb-4">
            <Label className="text-xs">Zusätzliche Services buchen (optional)</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {addons.map((a) => {
                const on = newOrder.addon_service_ids.includes(a.service_id);
                return (
                  <button key={a.service_id} type="button" onClick={() => toggleAddonInOrder(a.service_id)} className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "text-white" : "border-black/10 hover:border-black/30 bg-white"}`} style={on ? { background: brandColor, borderColor: brandColor } : {}} data-testid={`order-addon-${a.service_id}`}>
                    {a.name} <span className="opacity-70">· {eur(a.price_cents)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Button onClick={createOrder} disabled={busy} className="rounded-md text-white" style={{ background: brandColor }} data-testid="order-submit">
          Sonderauftrag anlegen <ArrowRight size={14} weight="bold" className="ml-1" />
        </Button>

        {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-2.5 mt-3" data-testid="order-msg"><CheckCircle size={14} weight="bold" className="inline mr-1" /> {msg}</div>}
        {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5 mt-3" data-testid="order-error">{err}</div>}

        {orders.length > 0 && (
          <div className="mt-6 pt-6 border-t border-black/5">
            <h4 className="text-sm font-semibold mb-2">Ihre Sonderaufträge ({orders.length})</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {orders.map((o) => (
                <div key={o.order_id} className="flex items-center justify-between text-xs p-3 border border-black/5 rounded-md" data-testid={`order-row-${o.order_id}`}>
                  <div className="flex items-center gap-3">
                    <CalendarBlank size={16} weight="bold" style={{ color: brandColor }} />
                    <div>
                      <div className="font-semibold">{new Date(o.scheduled_date).toLocaleDateString("de-DE")}</div>
                      {o.notes && <div className="text-zinc-500">{o.notes}</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold">{eur(o.price_cents)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{o.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
